/**
 * useConversationPersistence Hook
 * Manages Clara KI conversation persistence using clara_communication.db
 * 
 * Features:
 * - Auto-load conversation on mount
 * - Auto-save messages with JWT auth
 * - Session management with Supabase token
 * - Clear conversation
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { masterSupabase } from '../lib/supabase-master';

console.log('🔧 Clara Conversation Persistence Hook loaded');

const API_BASE = '/api/clara-conversations';

// Helper function to get JWT token from Supabase session
const getAuthToken = async () => {
  try {
    const { data: { session }, error } = await masterSupabase.auth.getSession();
    if (error || !session) {
      console.warn('⚠️ No session found');
      return null;
    }
    return session.access_token;
  } catch (error) {
    console.error('❌ Error getting auth token:', error);
    return null;
  }
};

// Helper function to make authenticated API calls
const authenticatedFetch = async (url, options = {}) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token available');
  }
  
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

export const useConversationPersistence = (user, isAuthenticated) => {
  const [conversationId, setConversationId] = useState(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [conversationError, setConversationError] = useState(null);
  const initRef = useRef({});

  // Initialize session and load conversation
  const initializeConversation = useCallback(async () => {
    console.log('🔄 Initializing conversation for user:', user?.id);
    if (!user || !isAuthenticated) {
      console.log('⚠️ Cannot initialize: user or isAuthenticated missing');
      return null;
    }
    
    // Create a unique key for this user
    const userKey = `init_${user.id}`;
    
    // Allow re-initialization if conversationId is already set (tab switch scenario)
    if (initRef.current[userKey] && conversationId) {
      console.log('🔄 Re-loading existing conversation:', conversationId);
      // Just reload messages for existing conversation
      try {
        const messagesResponse = await authenticatedFetch(
          `${API_BASE}/${conversationId}/messages?limit=200`
        );

        if (!messagesResponse.ok) {
          throw new Error('Failed to load messages');
        }

        const { messages } = await messagesResponse.json();
        // Mapping IDENTISCH zum Erst-Init-Zweig: type 'user'/'assistant' + id
        const mappedMessages = messages.map(msg => ({
          id: msg.message_id,
          type: msg.is_from_me ? 'user' : 'assistant',
          content: msg.content,
          timestamp: msg.timestamp,
          contentType: msg.content_type
        }));
        console.log(`✅ Reloaded ${mappedMessages.length} messages (mit Typ-Mapping)`);
        return mappedMessages;
      } catch (error) {
        console.error('❌ Error reloading messages:', error);
        return null;
      }
    }
    
    if (initRef.current[userKey]) {
      console.log('⚠️ Already initializing for this user, skipping');
      return null;
    }
    initRef.current[userKey] = true;
    setIsLoadingConversation(true);
    setConversationError(null);

    try {
      console.log('🔐 Getting JWT token from Supabase...');
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Failed to get authentication token');
      }
      
      console.log('📤 Sending session request with JWT token...');
      // Step 1: Create/Get session
      const sessionResponse = await fetch(`${API_BASE}/session`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          displayName: user.email?.split('@')[0] || 'User',
          email: user.email
        })
      });
      if (!sessionResponse.ok) {
        throw new Error('Failed to create session');
      }
      const { session } = await sessionResponse.json();
      console.log('✅ Session created:', session.session_id);
      
      // Step 2: Get/Create current conversation
      const conversationResponse = await authenticatedFetch(`${API_BASE}/current`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      if (!conversationResponse.ok) {
        throw new Error('Failed to get conversation');
      }
      const { conversation } = await conversationResponse.json();
      console.log('✅ Conversation loaded:', conversation.conversation_id);
      setConversationId(conversation.conversation_id);
      
      // Step 3: Load messages
      const messagesResponse = await authenticatedFetch(
        `${API_BASE}/${conversation.conversation_id}/messages?limit=200`
      );
      if (!messagesResponse.ok) {
        throw new Error('Failed to load messages');
      }
      const { messages } = await messagesResponse.json();
      console.log(`✅ Loaded ${messages.length} messages`);
      
      // Convert database messages to app format
      // KEIN 24h-Filter mehr: alle geladenen Nachrichten anzeigen (Limit 200)
      const now = Date.now();
      const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000);
      
      const formattedMessages = messages
        .filter(() => true)
        .map(msg => ({
          id: msg.message_id,
          type: msg.is_from_me ? 'user' : 'assistant',
          content: msg.content,
          timestamp: msg.timestamp,
          contentType: msg.content_type
        }));
      
      console.log(`✅ Showing ${formattedMessages.length} messages (Limit 200, kein 24h-Filter)`);

      setIsLoadingConversation(false);
      return formattedMessages;

    } catch (error) {
      console.error('❌ Error initializing conversation:', error);
      setConversationError(error.message);
      setIsLoadingConversation(false);
      return null;
    }
  }, [user, isAuthenticated, conversationId]);

  // Save a message to the database
  const saveMessage = useCallback(async (message) => {
    console.log('💾 Saving message:', message.id, message.type);
    if (!conversationId) {
      console.warn('⚠️ No conversation ID, skipping save');
      return false;
    }

    if (!isAuthenticated || !user) {
      console.warn('⚠️ User not authenticated, skipping save');
      return false;
    }

    try {
      const response = await authenticatedFetch(`${API_BASE}/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          messageId: message.id.toString(),
          senderId: message.type === 'user' ? user?.id : 'clara',
          senderName: message.type === 'user' ? (user?.email?.split('@')[0] || 'User') : 'Clara',
          content: message.content,
          contentType: message.contentType || 'text',
          isFromMe: message.type === 'user',
          timestamp: message.timestamp
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save message');
      }

      console.log('✅ Message saved:', message.id);
      return true;

    } catch (error) {
      console.error('❌ Error saving message:', error);
      return false;
    }
  }, [conversationId, user, isAuthenticated]);

  // Clear conversation (mark messages as deleted)
  const clearConversation = useCallback(async () => {
    if (!conversationId) return false;

    try {
      const response = await authenticatedFetch(`${API_BASE}/${conversationId}/messages`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to clear conversation');
      }

      console.log('✅ Conversation cleared');
      return true;
    } catch (error) {
      console.error('❌ Error clearing conversation:', error);
      return false;
    }
  }, [conversationId]);

  // Cleanup: Reset conversation on logout
  useEffect(() => {
    if (!isAuthenticated || !user) {
      console.log('🔄 User logged out, resetting conversation');
      setConversationId(null);
      initRef.current = {};
    }
  }, [isAuthenticated, user]);

  return {
    conversationId,
    isLoadingConversation,
    conversationError,
    initializeConversation,
    saveMessage,
    clearConversation
  };
};

export default useConversationPersistence;
